import type { ChangeLogEntryDto, ChangeLogFieldChangeDto } from '../../../../../api/created/changeLog.ts';
import type { UserDto } from '../../../../../api/generated/model';
import type {
  ChangeHistoryEntryData,
  HistoryAction,
  HistoryFieldChange,
} from '../../../../../components/history/historyTypes.ts';
import { getSalutationName } from '../../../../../utils/salutation.ts';
import { resolveChangedByName } from '../../../../../utils/user.ts';

export type ContactPersonFieldChange = HistoryFieldChange;

export type ContactPersonHistoryEntry = ChangeHistoryEntryData & {
  id: string;
  action: HistoryAction;
  actionLabel: string;
  changedByName: string;
};

export type ContactPersonHistoryMappingContext = {
  users: UserDto[];
};

const CONTACT_PERSON_FIELD_LABELS: Record<string, string> = {
  salutation: 'Anrede',
  last_name: 'Name',
  lastName: 'Name',
  first_name: 'Vorname',
  firstName: 'Vorname',
  function: 'Funktion',
  phone: 'Telefon Nr.',
  email: 'E-Mail',
  address_id: 'Institution',
  addressId: 'Institution',
  is_active: 'Aktiv',
  isActive: 'Aktiv',
  addresses: 'Institution',
};

const ACTION_LABELS: Record<HistoryAction, string> = {
  created: 'wurde erstellt von',
  updated: 'wurde geändert von',
  deleted: 'wurde gelöscht von',
};

function mapOperationToAction(operation: string): HistoryAction {
  const normalizedOperation = operation.trim().toUpperCase();

  if (normalizedOperation.includes('CREATE') || normalizedOperation.includes('INSERT')) {
    return 'created';
  }

  if (normalizedOperation.includes('DELETE') || normalizedOperation.includes('REMOVE')) {
    return 'deleted';
  }

  return 'updated';
}

function getContactPersonFieldLabel(fieldName: string) {
  return CONTACT_PERSON_FIELD_LABELS[fieldName] ?? fieldName;
}

function formatBooleanValue(value: string) {
  const normalizedValue = value.trim().toLowerCase();

  if (normalizedValue === 'true' || normalizedValue === '1') {
    return 'Ja';
  }

  if (normalizedValue === 'false' || normalizedValue === '0') {
    return 'Nein';
  }

  return value;
}

export function formatContactPersonFieldValue(fieldName: string, value: string | null | undefined) {
  if (value == null || !value.trim()) {
    return value ?? '';
  }

  switch (fieldName) {
    case 'salutation': {
      const name = getSalutationName(value);
      return name === '-' ? value : name;
    }
    case 'is_active':
    case 'isActive':
      return formatBooleanValue(value);
    default:
      return value;
  }
}

function mapFieldChange(change: ChangeLogFieldChangeDto): HistoryFieldChange {
  return {
    fieldLabel: getContactPersonFieldLabel(change.fieldName),
    oldValue: formatContactPersonFieldValue(change.fieldName, change.oldValue),
    newValue: formatContactPersonFieldValue(change.fieldName, change.newValue),
  };
}

export function mapChangeLogEntryToContactPersonHistoryEntry(
  entry: ChangeLogEntryDto,
  context: ContactPersonHistoryMappingContext,
): ContactPersonHistoryEntry {
  const action = mapOperationToAction(entry.operation);

  return {
    id: entry.changeSetId,
    date: entry.changedAt,
    action,
    actionLabel: ACTION_LABELS[action],
    changedByName: resolveChangedByName(entry.changedBy, context.users),
    changes: entry.changes.map((change) => mapFieldChange(change)),
  };
}

export function mapChangeLogEntriesToContactPersonHistory(
  entries: ChangeLogEntryDto[],
  context: ContactPersonHistoryMappingContext,
): ContactPersonHistoryEntry[] {
  return entries.map((entry) => mapChangeLogEntryToContactPersonHistoryEntry(entry, context));
}
